import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { MaskedLine } from "./anim";
import { goToApp } from "@/lib/appUrl";

const HERO_IMG =
  "https://images.unsplash.com/photo-1592700819903-308f4820372d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMHN0dWRpbyUyMHByb2R1Y2VyJTIwZGFyayUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTQ3MjI1Mnww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} className="relative pt-32 md:pt-44 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 nz-glow pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-slate-400">
            Your Music Distribution
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-display font-medium tracking-tighter leading-[0.95] text-white text-5xl sm:text-6xl md:text-7xl lg:text-[6.2rem]">
              <MaskedLine delay={0.05}>Get your</MaskedLine>
              <MaskedLine delay={0.16}>
                song <span className="text-blue-500">heard</span>
              </MaskedLine>
              <MaskedLine delay={0.27}>everywhere.</MaskedLine>
            </h1>
          </div>

          <div className="lg:col-span-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="text-base sm:text-lg text-slate-400 leading-relaxed"
            >
              Noizbloc delivers your music to Spotify, Apple Music and 150+ stores.
              Open to every artist and every label who takes the craft seriously.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.62 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => { if (!goToApp("/register")) navigate("/register"); }}
                className="group inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-6 py-3.5 hover:bg-slate-200 active:scale-[0.98] transition-all"
              >
                Start distributing
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => document.querySelector("#how")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-6 py-3.5 hover:bg-white/5 transition-colors"
              >
                <Play size={16} /> How it works
              </button>
            </motion.div>
          </div>
        </div>

        {/* Spotlighted product photography, clipped frame + parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 md:mt-20 rounded-3xl overflow-hidden border border-white/10 h-[46vh] md:h-[62vh]"
        >
          <motion.img
            style={{ y: imgY }}
            src={HERO_IMG}
            alt="Producer in the studio"
            className="absolute inset-0 w-full h-[130%] object-cover object-center opacity-80 grayscale-[15%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/30 to-transparent" />
          <motion.div style={{ y: overlayY }} className="absolute bottom-0 inset-x-0 p-6 md:p-10">
            <div className="flex flex-wrap items-end justify-between gap-6">
              {[
                { k: "150+", v: "Stores & platforms" },
                { k: "0%", v: "Commission on royalties" },
                { k: "48h", v: "Average time to live" },
              ].map((s) => (
                <div key={s.v}>
                  <div className="font-display text-3xl md:text-5xl font-medium text-white">{s.k}</div>
                  <div className="text-xs md:text-sm text-slate-400 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
